/**
 * Who's signed in decides what shows: sign-in, the terms of use, or the app. FMS staff pick a
 * customer account first; customers go straight to their own.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { api, ApiError, hasToken, onSignedOut, read, setToken, write, type Account, type Me } from "./api.ts";
import { Invite, SignIn, TermsGate } from "./screens/auth.tsx";
import { DevicePage } from "./screens/device.tsx";
import { Alerts, AlertsContext, useAlerts } from "./screens/alerts.tsx";
import { AskAi } from "./screens/ai.tsx";
import { Reports } from "./screens/reports.tsx";
import { HomePinsProvider } from "./home-pins.tsx";
import { PlanProvider, TrialBanner } from "./plan.tsx";
import { NotificationsCard } from "./notifications-card.tsx";
import { Farm } from "./screens/farm.tsx";
import { Admin } from "./screens/admin.tsx";
import { ProgramEditor, Programs } from "./screens/programs.tsx";
import { can } from "./control.ts";
import { Devices } from "./screens/devices.tsx";
import { go, useRoute, type Route } from "./route.ts";
import { ListenContext } from "./listen.tsx";
import { NewVersionBanner } from "./new-version.tsx";
import { HelpButton, HelpPanel, Tour, useTour } from "./help.tsx";
import { PayPalPage, usePayPalReturn } from "./screens/paypal.tsx";
import { ErrorBoundary, Icon, lazyScreen, Logo, Problem, Spinner, useLoad, Wordmark } from "./ui.tsx";

const ACCOUNT_KEY = "tc.account";

// Staff, installer and now-and-then screens: their code loads the first time they're opened,
// so the app a farmer opens every morning is smaller. Farm, devices, a device, alerts and
// programs stay in the first download.
const Customers = lazyScreen(() => import("./screens/customers.tsx").then((m) => m.Customers));
const AccountPage = lazyScreen(() => import("./screens/customers.tsx").then((m) => m.AccountPage));
const NewCustomer = lazyScreen(() => import("./screens/customers.tsx").then((m) => m.NewCustomer));
const DeviceSetup = lazyScreen(() => import("./screens/setup.tsx").then((m) => m.DeviceSetup));
const FarmMap = lazyScreen(() => import("./screens/map.tsx").then((m) => m.FarmMap));
const ServerPage = lazyScreen(() => import("./screens/server.tsx").then((m) => m.ServerPage));
const ShadowPage = lazyScreen(() => import("./screens/shadow.tsx").then((m) => m.ShadowPage));
const SupportPage = lazyScreen(() => import("./screens/support.tsx").then((m) => m.SupportPage));
const ConsolePage = lazyScreen(() => import("./screens/console.tsx").then((m) => m.ConsolePage));
const ToolboxPage = lazyScreen(() => import("./screens/toolbox.tsx").then((m) => m.ToolboxPage));

export function App({ banner, signInHint }: { banner?: ReactNode; signInHint?: ReactNode }) {
  const invite = location.pathname.replace(/\/$/, "").endsWith("/invite") ? location.hash.slice(1) : "";
  const [me, setMe] = useState<Me | null>(null);
  const [state, setState] = useState<"loading" | "signed-out" | "ready" | "error">(hasToken() ? "loading" : "signed-out");
  const [error, setError] = useState<unknown>();

  const load = useCallback(async () => {
    if (!hasToken()) return setState("signed-out");
    setState("loading");
    try {
      setMe(await api.me());
      setState("ready");
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return setState("signed-out");
      setError(e);
      setState("error");
    }
  }, []);

  useEffect(() => {
    if (!invite) void load();
    return onSignedOut(() => {
      setMe(null);
      setState("signed-out");
    });
  }, [load, invite]);

  const signOut = async () => {
    try {
      await api.logout();
    } catch {
      /* signed out locally either way */
    }
    setToken(null);
    write(ACCOUNT_KEY, null);
    setMe(null);
    setState("signed-out");
  };

  if (invite) {
    return (
      <Invite
        inviteToken={invite}
        onDone={() => {
          // Leave the invite address so a reload doesn't try the used link again.
          history.replaceState(null, "", location.pathname.replace(/invite\/?$/, "") + "#/devices");
          location.reload();
        }}
      />
    );
  }
  if (state === "signed-out") return <SignIn onSignedIn={load} hint={signInHint} />;
  if (state === "error") {
    return (
      <div className="view">
        <Problem error={error} onRetry={load} />
      </div>
    );
  }
  if (state === "loading" || !me) return <Spinner />;
  if (me.user.role !== "fms-admin" && me.account?.status === "suspended") return <TurnedOff me={me} onSignOut={signOut} />;
  if (me.termsRequired) return <TermsGate me={me} onAccepted={load} onSignOut={signOut} />;
  return (
    <PlanProvider plan={me.plan}>
      <HomePinsProvider key={me.user.id} initial={me.user.home}>
        <Shell me={me} onSignOut={signOut} banner={banner} />
      </HomePinsProvider>
    </PlanProvider>
  );
}

function Shell({ me, onSignOut, banner }: { me: Me; onSignOut: () => void; banner?: ReactNode }) {
  const route = useRoute();
  const staff = me.user.role === "fms-admin";
  const [picked, setPicked] = useState<string | undefined>(() => (staff ? (read(ACCOUNT_KEY) ?? undefined) : undefined));
  const accountId = staff ? picked : (me.account?.id ?? undefined);

  // Staff need the account's name and its site names for headings.
  // Re-read every few minutes: a farm cut over (or handed back) shows without a reload.
  // The first time, a customer's farm comes from sign-in (no second /me on the way in: on one bar
  // of signal every request counts); every 5 minutes after, it's read again.
  const firstRead = useRef(true);
  const accounts = useLoad(
    // A failed re-read keeps the last good list (useLoad), never the copy from sign-in.
    () => {
      if (!staff && firstRead.current) {
        firstRead.current = false;
        return Promise.resolve(me.account ? [me.account] : []);
      }
      return staff ? api.accounts().then((r) => r.accounts) : api.me().then((m) => (m.account ? [m.account] : []));
    },
    [staff],
    300_000,
  );
  const account: Account | undefined = accounts.data?.find((a) => a.id === accountId) ?? (staff ? undefined : (me.account ?? undefined));
  const sites = useLoad(() => (accountId ? api.sites(staff ? accountId : undefined).then((r) => r.sites) : Promise.resolve([])), [accountId, staff]);
  const siteName = (id: string) => sites.data?.find((s) => s.id === id)?.name;
  // Whether the server only listens. Not known (first call failed) counts as listening, so
  // switches never look usable when they aren't; re-read every few minutes.
  const health = useLoad(() => api.health(), [], 300_000);
  const [helpOpen, setHelpOpen] = useState(false);
  const payNote = usePayPalReturn(accountId);
  const tour = useTour(me);
  const ai = useLoad(() => api.aiStatus(staff ? accountId : undefined).catch(() => ({ ask: false, farmData: false })), [accountId, staff]);
  const startTour = () => {
    setHelpOpen(false);
    tour.start();
  };

  const pick = (a: Account) => {
    setPicked(a.id);
    write(ACCOUNT_KEY, a.id);
    go({ name: "devices" });
  };

  // Staff with no account chosen start on the customer list.
  const effective: Route = staff && !accountId && (["farm", "devices", "device", "device-setup", "alerts", "admin", "programs", "program", "ask", "reports", "map"] as string[]).includes(route.name) ? { name: "accounts" } : route;
  const onStaffPage = effective.name === "accounts" || effective.name === "account" || effective.name === "new-account";
  const alerts = useAlerts(accountId, staff);
  const openAlerts = (alerts.data?.alerts ?? []).filter((a) => !a.acked && a.severity !== "info").length;
  // Staff: how many tickets are waiting for an answer, for the Tickets tab's badge.
  const ticketsOpen = useLoad(() => (staff ? api.allTickets("open").then((r) => r.open) : Promise.resolve(0)), [staff], 120_000);
  const showAdmin = can(me.user.role, "manage-users") && (!staff || !!accountId);
  type Tab = { route: Route; label: string; icon: "home" | "devices" | "alerts" | "accounts" | "me" | "admin" | "programs" | "spark" | "chart" | "map" | "server" | "antenna" | "usb"; badge?: number; phone?: boolean };
  // Five tabs fit on a phone: Admin and Customers are reached from the Me page there.
  const tabs: Tab[] = [
    { route: { name: "farm" }, label: "Farm", icon: "home" },
    { route: { name: "devices" }, label: "Devices", icon: "devices" },
    { route: { name: "programs" }, label: "Programs", icon: "programs" },
    { route: { name: "alerts" }, label: "Alerts", icon: "alerts", badge: openAlerts },
    // On a phone, Ask AI is reached from the farm home's Insights and Reports from the Me page.
    { route: { name: "map" }, label: "Map", icon: "map", phone: false },
    { route: { name: "reports" }, label: "Reports", icon: "chart", phone: false },
    { route: { name: "ask" }, label: "Ask AI", icon: "spark", phone: false },
    ...(showAdmin ? [{ route: { name: "admin" } as Route, label: "Admin", icon: "admin" as const, phone: false }] : []),
    ...(staff ? [{ route: { name: "accounts" } as Route, label: "Customers", icon: "accounts" as const, phone: false }] : []),
    ...(staff ? [{ route: { name: "support" } as Route, label: "Tickets", icon: "alerts" as const, phone: false, badge: ticketsOpen.data ?? 0 }] : []),
    ...(staff ? [{ route: { name: "shadow" } as Route, label: "Shadow check", icon: "programs" as const, phone: false }] : []),
    ...(staff || me.user.role === "installer" ? [{ route: { name: "console" } as Route, label: "Console", icon: "antenna" as const, phone: false }] : []),
    ...(staff || me.user.role === "installer" ? [{ route: { name: "toolbox" } as Route, label: "USB Toolbox", icon: "usb" as const, phone: false }] : []),
    ...(staff ? [{ route: { name: "server" } as Route, label: "Server", icon: "server" as const, phone: false }] : []),
    ...(staff ? [{ route: { name: "paypal" } as Route, label: "PayPal", icon: "accounts" as const, phone: false }] : []),
    { route: { name: "me" }, label: "Me", icon: "me" },
  ];

  const isCurrent = (r: Route) =>
    r.name === "devices"
      ? effective.name === "devices" || effective.name === "device" || effective.name === "device-setup"
      : r.name === "accounts"
        ? onStaffPage
        : r.name === "programs"
          ? effective.name === "programs" || effective.name === "program"
          : effective.name === r.name;

  let body: ReactNode;
  if (effective.name === "accounts") body = <Customers current={accountId} onView={pick} />;
  else if (effective.name === "account") body = <AccountPage key={effective.id} id={effective.id} onView={pick} />;
  else if (effective.name === "new-account") body = <NewCustomer />;
  else if (effective.name === "server" && staff) body = <ServerPage />;
  else if (effective.name === "console" && (staff || me.user.role === "installer"))
    body = <ConsolePage tab={effective.tab} staff={staff} onOpenDevice={(eui: string) => go({ name: "device", eui })} />;
  else if (effective.name === "toolbox" && (staff || me.user.role === "installer")) body = <ToolboxPage />;
  else if (effective.name === "paypal" && staff) body = <PayPalPage />;
  else if (effective.name === "shadow" && staff)
    body = (
      <ShadowPage
        onOpenFarm={(id: string) => {
          setPicked(id);
          write(ACCOUNT_KEY, id);
          go({ name: "programs" });
        }}
      />
    );
  else if (effective.name === "support") body = <SupportPage key={`${effective.id ?? ""}${accountId ?? ""}`} accountId={accountId} staff={staff} id={effective.id} screen={effective.screen} />;
  else if (effective.name === "me") body = <MePage me={me} onSignOut={onSignOut} showAdmin={showAdmin} staff={staff} accountId={accountId} accountName={account?.name} onTour={startTour} />;
  else if (effective.name === "device") body = <DevicePage key={effective.eui} eui={effective.eui} siteName={siteName} role={me.user.role} myId={me.user.id} />;
  else if (effective.name === "device-setup") body = <DeviceSetup key={effective.eui} eui={effective.eui} role={me.user.role} />;
  else if (effective.name === "programs") body = <Programs accountId={accountId} staff={staff} role={me.user.role} />;
  else if (effective.name === "program") body = <ProgramEditor key={effective.id} id={effective.id} accountId={accountId} staff={staff} />;
  else if (effective.name === "admin") body = <Admin key={effective.tab ?? ""} accountId={accountId} staff={staff} role={me.user.role} myId={me.user.id} sites={sites.data ?? []} tab={effective.tab} onSitesChanged={sites.reload} />;
  else if (effective.name === "map") body = <FarmMap key={accountId ?? "mine"} accountId={accountId} staff={staff} role={me.user.role} account={account} />;
  else if (effective.name === "reports") body = <Reports key={accountId ?? "mine"} accountId={accountId} staff={staff} seasonStartMonth={account?.settings?.seasonStartMonth} />;
  else if (effective.name === "ask") body = <AskAi key={accountId ?? "mine"} accountId={accountId} staff={staff} />;
  else if (effective.name === "alerts") body = <Alerts key={effective.tab ?? ""} accountId={accountId} staff={staff} sites={sites.data ?? []} role={me.user.role} tab={effective.tab} />;
  else if (effective.name === "devices") body = <Devices accountId={staff ? accountId : undefined} />;
  else body = <Farm accountId={accountId} staff={staff} />;

  return (
    <ListenContext.Provider value={(health.data ? health.data.listenOnly === true : true) && !account?.inControl}>
    <AlertsContext.Provider value={alerts}>
    <div className="app">
      <aside className="side" aria-label="Main">
        <div className="brand">
          <Logo size={34} />
          <div>
            <Wordmark />
            <div className="brand-sub">Remote water and pump control</div>
          </div>
        </div>
        <nav className="nav">
          {tabs.map((t) => (
            <button key={t.label} data-tour={t.route.name} aria-current={isCurrent(t.route) ? "page" : undefined} onClick={() => go(t.route)}>
              <Icon name={t.icon} /> {t.label}
              {t.badge ? <span className="badge">{t.badge}</span> : null}
            </button>
          ))}
        </nav>
        <div className="side-foot">
          <span>{me.user.name}</span>
          <span>{staff ? "FMS staff" : me.user.role}</span>
        </div>
      </aside>
      <main>
        <header className="topbar">
          <span className="mob-brand">
            <Logo size={30} />
          </span>
          <div className="farm">
            {onStaffPage ? (
              <>
                <span className="farm-name">Customers</span>
                <span className="farm-meta">FMS staff</span>
              </>
            ) : (
              <>
                <span className="farm-name">{account?.name ?? (staff ? "Choose a customer" : "Think-Control")}</span>
                <span className="farm-meta">
                  {account
                    ? `${sites.data?.length ?? "…"} site${sites.data?.length === 1 ? "" : "s"}${account.status === "setup" ? " · in set-up" : account.status === "suspended" ? " · turned off" : ""}`
                    : ""}
                </span>
              </>
            )}
          </div>
          <div className="top-right">
            {staff && account && !onStaffPage && (
              <>
                <button className="btn" onClick={() => go({ name: "account", id: account.id })}>
                  Account
                </button>
                <button className="btn" onClick={() => go({ name: "accounts" })}>
                  Switch
                </button>
              </>
            )}
          </div>
        </header>
        <div className="view">
          {banner}
          <NewVersionBanner />
          {payNote && <div className="banner info">{payNote}</div>}
          <TrialBanner />
          {/* A screen that breaks shows "Try again" instead of a blank app; a new page starts clean. */}
          <ErrorBoundary resetKey={JSON.stringify(effective)}>{body}</ErrorBoundary>
        </div>
      </main>
      <nav className="tabbar" aria-label="Main">
        {tabs.filter((t) => t.phone !== false).map((t) => (
          <button key={t.label} data-tour={t.route.name} aria-current={isCurrent(t.route) ? "page" : undefined} onClick={() => go(t.route)}>
            <Icon name={t.icon} size={22} />
            {t.label}
            {t.badge ? <span className="badge">{t.badge}</span> : null}
          </button>
        ))}
      </nav>
      {effective.name !== "ask" && !helpOpen && <HelpButton onOpen={() => setHelpOpen(true)} />}
      {helpOpen && <HelpPanel accountId={accountId} staff={staff} screen={SCREEN_NAMES[effective.name] ?? "Farm home"} onClose={() => setHelpOpen(false)} onTour={startTour} />}
      {tour.on && <Tour me={me} farmData={!!ai.data?.farmData} onDone={tour.done} />}
    </div>
    </AlertsContext.Provider>
    </ListenContext.Provider>
  );
}

/** What the help bot is told about where the person is. */
const SCREEN_NAMES: Partial<Record<Route["name"], string>> = {
  farm: "Farm home",
  devices: "Devices",
  device: "device page",
  "device-setup": "device Set up",
  programs: "Programs",
  program: "program editor",
  alerts: "Alerts",
  admin: "Admin",
  map: "Farm map",
  reports: "Reports",
  me: "Me",
  accounts: "Customers",
  account: "customer account",
  "new-account": "new customer",
  console: "Console",
  toolbox: "USB Toolbox",
  server: "Server",
  paypal: "PayPal",
  shadow: "Shadow check",
  support: "Help and support",
};

function MePage({
  me,
  onSignOut,
  showAdmin,
  staff,
  accountId,
  accountName,
  onTour,
}: {
  me: Me;
  onSignOut: () => void;
  showAdmin: boolean;
  staff: boolean;
  accountId?: string;
  accountName?: string;
  onTour: () => void;
}) {
  const roles: Record<string, string> = {
    owner: "Owner",
    manager: "Manager",
    operator: "Operator",
    viewer: "Viewer",
    installer: "Installer",
    "fms-admin": "FMS staff",
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 560 }}>
      <h2 style={{ fontSize: 22 }}>{me.user.name}</h2>
      <dl className="kv">
        <div>
          <dt>Email</dt>
          <dd>{me.user.email}</dd>
        </div>
        <div>
          <dt>Role</dt>
          <dd>{roles[me.user.role] ?? me.user.role}</dd>
        </div>
        {me.account && (
          <div>
            <dt>Account</dt>
            <dd>{me.account.name}</dd>
          </div>
        )}
      </dl>
      <NotificationsCard accountId={accountId} accountName={accountName} staff={staff} />
      <div className="toolbar">
        {staff && (
          <button className="btn" onClick={() => go({ name: "accounts" })}>
            Customers
          </button>
        )}
        {staff && (
          <button className="btn" onClick={() => go({ name: "shadow" })}>
            Shadow check
          </button>
        )}
        {staff && (
          <button className="btn" onClick={() => go({ name: "server" })}>
            Server health
          </button>
        )}
        {(staff || me.user.role === "installer") && (
          <button className="btn" onClick={() => go({ name: "console" })}>
            Console: live traffic and commands
          </button>
        )}
        {staff && (
          <button className="btn" onClick={() => go({ name: "paypal" })}>
            PayPal: who pays
          </button>
        )}
        {(staff || me.user.role === "installer") && (
          <button className="btn" onClick={() => go({ name: "toolbox" })}>
            USB Toolbox: set up a MultiPro by cable
          </button>
        )}
        <button className="btn" onClick={() => go({ name: "map" })}>
          Farm map
        </button>
        <button className="btn" onClick={() => go({ name: "reports" })}>
          Reports: water, run hours, energy
        </button>
        {showAdmin && (
          <button className="btn" onClick={() => go({ name: "admin" })}>
            Admin: people, roles, settings
          </button>
        )}
        <button className="btn" onClick={() => go({ name: "support" })}>
          {staff ? "Support tickets" : "Help and support: ask FMS"}
        </button>
        <button className="btn" onClick={onTour}>
          Show me around the app
        </button>
        <button className="btn" onClick={onSignOut}>
          Sign out
        </button>
      </div>
    </section>
  );
}

/** A customer whose account FMS has turned off: say so plainly instead of showing errors. */
function TurnedOff({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  return (
    <div className="view">
      <div className="off-screen">
        <Logo size={48} />
        <h1 style={{ fontSize: 26 }}>{me.account?.name} is turned off</h1>
        <p className="muted" style={{ margin: 0 }}>
          Think-Control for this account has been paused. Your devices are still reporting and nothing has been lost. Please contact FMS to turn it back on.
        </p>
        <button className="btn" onClick={onSignOut}>
          Sign out
        </button>
      </div>
    </div>
  );
}
